import React, { ReactNode, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Button } from 'is24-corecss';
import { FormattedMessage } from 'react-intl';
import PageWrapper from '../../components/pageWrapper/PageWrapper';
import ErrorMessage from './components/ErrorMessage';
import { fetchInvitation, performLifecycleTransition } from '../../api/TeamMemberApi';
import { TeamMemberLifecycleEvent } from '../../models/TeamMemberLifecycleEvent';
import styles from './InvitationAcceptance.module.css';
import { useFetching } from '../../common/hooks/useFetching';
import { Invitation } from '../../models/Invitation';
import { getLogger } from '../../common/logger/logger';

type ParamTypes = {
  teamMemberId: string;
  invitationId: string;
};

const useQuery = () => new URLSearchParams(useLocation().search);

const isActive = (invitation?: Invitation) => invitation && invitation.status == 'ACTIVE';

const mailTo = (email: string) => 'mailto:' + email;

const EmailLink = (email: ReactNode) => <a href={mailTo(email as string)}>{email}</a>;

const Strong = (text: ReactNode) => <strong>{text}</strong>;

const InvitationAcceptancePage: React.FC = () => {
  const log = getLogger('InvitationAcceptancePage');
  const { teamMemberId, invitationId } = useParams<ParamTypes>();
  const lifecycleEvent = { command: 'acceptInvitation', invitationId: invitationId } as TeamMemberLifecycleEvent;
  const { isLoading, data: invitation, error, setError } = useFetching(fetchInvitation, [teamMemberId!, invitationId!]);
  const query = useQuery();
  const showLandingPage = query.get('showLandingPage');

  const performLifecycle = async () => {
    try {
      const response = await performLifecycleTransition(teamMemberId!, lifecycleEvent);
      if (response && response.possibleTransitions) {
        const transition = response.possibleTransitions.find((element) => !!element['activate']);
        const redirectionLink = transition?.activate?.precondition?.redirectTo;
        if (redirectionLink) {
          window.location.assign(redirectionLink as string);
        } else {
          throw new Error('The invitation activation redirectTo link is not present');
        }
      }
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    const invitationLoaded = !isLoading && !error && invitation;
    if (invitationLoaded && !isActive(invitation)) {
      setError(new Error(`The invitation ${invitationId} is not active`));
    }
    if (invitationLoaded && isActive(invitation) && !showLandingPage) {
      performLifecycle();
    }
  }, [isLoading]);

  useEffect(() => {
    error && log.error(error);
  }, [error]);

  return (
    <PageWrapper title='Konto-Aktivierung' isWhiteHeader={true}>
      <div className='margin-vertical-xl align-center'>
        {!isLoading && (error || !isActive(invitation)) && <ErrorMessage teamMemberId={teamMemberId!} invitationId={invitationId!} />}
      </div>
      {!error && showLandingPage && invitation && isActive(invitation) && (
        <>
          <h1 className='font-light'>
            <FormattedMessage id='teammember.invitation.headline' />
          </h1>

          <p className='margin-top-xl margin-bottom-xl'>
            <FormattedMessage id='teammember.invitation.salutation' values={{ invitee: invitation.invitee }} />
          </p>

          <p>
            <FormattedMessage
              id='teammember.invitation.text1'
              values={{
                strong: (chunks: ReactNode[]) => Strong(chunks),
                inviter: invitation.inviter,
              }}
            />
          </p>
          <br />
          <p>
            <FormattedMessage id='teammember.invitation.text2' />
          </p>
          <br />
          <p className={`${styles.hintText} background-info`}>
            <span className={`${styles.hintIcon} font-info is24-icon-info`} />
            <FormattedMessage
              id='teammember.invitation.text3'
              values={{
                strong: Strong,
              }}
            />
          </p>
          <br />
          <p>
            <FormattedMessage id='teammember.invitation.text4' />
          </p>
          <div className='margin-vertical-xl'>
            <Button appearance='primary' data-testid='continue-button' onClick={performLifecycle}>
              <FormattedMessage id='teammember.invitation.button.continue' />
            </Button>
          </div>
          <p className='font-hint'>
            <FormattedMessage
              id='teammember.invitation.text5'
              values={{
                a: (chunks: ReactNode) => EmailLink(chunks),
                strong: (chunks: ReactNode) => Strong(chunks),
                inviter: invitation.inviter,
              }}
            />
          </p>
        </>
      )}
    </PageWrapper>
  );
};
export default InvitationAcceptancePage;
